import React, { useRef } from 'react';
import { connect } from 'react-redux';
import parseArrayToObject from 'src/universal/utils/parseArrayToObject';
import replaceStringWithVars from 'src/universal/utils/replaceStringWithVars';
import {
  Element,
  FontType,
  Color,
  ContentTileWithImageCarousel,
  Typography
} from '@americanexpress/aqx-components';
import { getPageFirstCardData, getPageFeatureFlags } from 'src/universal/redux/page';
import { isG157Variant } from 'src/universal/components/Experiments/G157/redux';
import G157Carousel from 'src/universal/components/Experiments/G157/BusinessValueTilesCarousel';
import MobileSlide from './MobileSlide';
import LazyScrollMetricTracker from 'src/universal/components/DTM/LazyScrollMetricTracker';
import useTrackShopComponents from 'src/universal/hooks/useTrackShopComponents';
import { Sanitizer } from 'acq-components/lib/base-components/Sanitizer/Sanitizer';
import {
  sendDTMClickEvent,
  sendDTMCustomEvent
} from 'service_modules/models/dtm-tagging';
import { useBreakpoint } from '@americanexpress/aqx-components';

import styles from './__styles__/index.scss?modules';

const impressedIndexList = [];

const FeaturedBenefitsCarouselBox = ({
  featuredBenefitsCarouselData,
  featuredBenefitsFeatureFlag,
  isG157
}) => {
  if (!featuredBenefitsCarouselData || !featuredBenefitsFeatureFlag) {
    return null;
  }
  const { header, content, dtmEvents, withFlag } = featuredBenefitsCarouselData;

  const dtmObj = parseArrayToObject(dtmEvents, 'key');

  const getImpressionDtm = slideNumber => {
    return {
      ...dtmObj.featuredBenefitsCarouselImpression,
      eventAsset: replaceStringWithVars({
        varsObject: {
          slideNum: slideNumber
        },
        string: dtmObj.featuredBenefitsCarouselImpression.eventAsset
      })
    };
  };

  const getClickDtm = buttonName => {
    return {
      ...dtmObj.featuredBenefitsCarouselClick,
      eventAsset: replaceStringWithVars({
        varsObject: {
          button: buttonName
        },
        string: dtmObj.featuredBenefitsCarouselClick.eventAsset
      })
    };
  };

  const slidesLength = featuredBenefitsCarouselData.content.length;

  const slideNum = useRef(1);

  /*
  US4500243:
  (1) The problem:  useTrackShopComponents has page-leval scope. It was meant to
      track events for the entire page.  The original intention to bring it in is for
      tracking just this component.  The side effect is that clicking any other
      unrelated components will trigger the handlers defined below.  
  (2) The solution: the fix below basically adds filter to exclude all other unrelated events.
  */
  useTrackShopComponents({
    trackingFn: () => ([type, { key }]) => {
      if (key.indexOf('featuredbenefits:carousel') <= -1) {
        return;
      }
      if (type === 'click' && key.indexOf('dot') > -1) {
        slideNum.current = parseInt(key[key.length - 1]);

        sendDTMClickEvent(getClickDtm(`Dot${slideNum.current}`));
      }
      if (type === 'click' && key.indexOf('left') > -1) {
        if (slideNum.current === 1) {
          slideNum.current = slidesLength;
        } else {
          slideNum.current = slideNum.current - 1;
        }

        sendDTMClickEvent(getClickDtm('Leftarr'));
      }
      if (type === 'click' && key.indexOf('right') > -1) {
        if (slideNum.current === slidesLength) {
          slideNum.current = 1;
        } else {
          slideNum.current = slideNum.current + 1;
        }
        sendDTMClickEvent(getClickDtm('Rightarr'));
      }

      if (impressedIndexList.indexOf(slideNum.current) <= -1) {
        impressedIndexList.push(slideNum.current);
        sendDTMCustomEvent(getImpressionDtm(slideNum.current));
      }
    }
  });

  const itemHeaderList = [];

  const slideData = content.map(eachSlide => {
    const { assets, header, text, flagText, title } = eachSlide;
    const assetObject = parseArrayToObject(assets, 'breakpoint');
    itemHeaderList.push(header);

    return {
      imageUrl: {
        desktop: assetObject.desktop.url,
        mobile: assetObject.mobile ? assetObject.mobile.url : null
      },
      header: (
        <Sanitizer className={styles.headerStyles} safelySetInnerHTML={header} />
      ),
      text: text,
      flagText: flagText,
      imageTitle: title
    };
  });

  const { sm } = useBreakpoint();
  const needG157Carousel = sm && isG157;
  let slideList = null;

  if (needG157Carousel) {
    slideList = slideData.map((item, i) => {
      return <MobileSlide key={`k${i}`} index={i} {...item} />;
    });
  }

  return (
    <Element
      className={styles.featuredBenefitsCarouselBox}
      theme={{
        marginBottom: 30
      }}
    >
      <Typography
        Component="h2"
        $size={20}
        $lineHeight={24}
        theme={{ color: Color.Black, marginBottom: 20 }}
        $family={FontType.BentonRegular}
      >
        {header}
      </Typography>

      {!needG157Carousel && (
        <ContentTileWithImageCarousel
          qeId="FeaturedBenefitsCarousel"
          customization={{ headerHtmlTag: 'h3' }}
          withFlag={withFlag}
          data={{
            slides: slideData
          }}
        />
      )}

      {needG157Carousel && (
        <div className={styles.g157Wrap}>
          <G157Carousel
            slideList={slideList}
            needHeaderBox={false}
            originKey="carouselBox"
            itemHeaderList={itemHeaderList}
          />
        </div>
      )}

      <LazyScrollMetricTracker
        scrollDtm={getImpressionDtm(slideNum.current)}
        startTrackingAfterPznArrival={true}
      />
    </Element>
  );
};

const mapStateToProps = state => {
  return {
    featuredBenefitsCarouselData: getPageFirstCardData(state)
      .featuredBenefitsCarousel,
    featuredBenefitsFeatureFlag: getPageFeatureFlags(state)
      .showFeaturedBenefitsCarousel,
    isG157: isG157Variant(state)
  };
};

export default connect(mapStateToProps)(FeaturedBenefitsCarouselBox);
